import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Input, Modal, Select, Skeleton, useToast } from '../../../design-system';

// "Attach this chat file to the client's records."
//
// A file arrives in chat as category OTHER, linked to the message it came in on
// — findable, but loose. This is where staff put it somewhere meaningful: a
// category from the firm's taxonomy, and optionally a specific task, filing
// period or request.
//
// Staff-only: the portal never renders the menu that opens this, and there is
// no portal endpoint behind it. The destination list alone (the firm's tasks
// and working periods) is not a client's business.

const DESTINATIONS = [
  { value: '', label: 'Client records (no specific item)' },
  { value: 'Task', label: 'A task' },
  { value: 'ComplianceInstance', label: 'A filing period' },
  { value: 'ClientRequest', label: 'A client request' },
];

// FY choices around today, newest first — an Indian FY starts in April.
function fyOptions() {
  const now = new Date();
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return Array.from({ length: 6 }, (_, i) => {
    const y = startYear + 1 - i;
    return `${y}-${String((y + 1) % 100).padStart(2, '0')}`;
  });
}

function FileToClientModal({ clientId, clientName, file, onClose, onFiled }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [category, setCategory] = useState('OTHER');
  const [destType, setDestType] = useState('');
  const [destId, setDestId] = useState('');
  const [financialYear, setFinancialYear] = useState('');
  const [period, setPeriod] = useState('');
  const [title, setTitle] = useState(file?.fileName || '');
  const [targetSearch, setTargetSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get(`/api/chat/threads/${clientId}/file-targets`)
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && toast.danger(e.response?.data?.error || 'Could not load filing destinations'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Categories grouped into their folders, so a 49-item flat list doesn't
  // become an unreadable dropdown.
  const grouped = useMemo(() => {
    if (!data) return [];
    return (data.groups || []).map((g) => ({
      ...g,
      items: (data.categories || []).filter((c) => c.group === g.id),
    })).filter((g) => g.items.length);
  }, [data]);

  const targets = useMemo(() => {
    const list = (data?.targets?.[destType] || []);
    const q = targetSearch.trim().toLowerCase();
    return q ? list.filter((t) => t.label.toLowerCase().includes(q)) : list;
  }, [data, destType, targetSearch]);

  const save = async () => {
    if (destType && !destId) { toast.danger('Choose what to attach it to'); return; }
    setSaving(true);
    try {
      const r = await apiClient.patch(
        `/api/chat/threads/${clientId}/attachments/${file.documentId}/file`,
        {
          category,
          title: title.trim() || undefined,
          financialYear: financialYear || null,
          period: period.trim() || null,
          linkedToType: destType || null,
          linkedToId: destType ? destId : null,
        }
      );
      const where = destType
        ? (data?.targets?.[destType] || []).find((t) => t.id === destId)?.label
        : `${clientName}'s records`;
      toast.success(`Filed to ${where}`);
      onFiled?.(r.data);
      onClose();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not file that document');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Attach to client records"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={saving} disabled={loading || saving}>
            File it
          </Button>
        </>
      }
    >
      {loading ? (
        <Skeleton.Text lines={7} />
      ) : (
        <div className="chat-file-form">
          <div className="chat-file-target">
            <span className="chat-att-icon" aria-hidden>📎</span>
            <div>
              <strong>{file.fileName}</strong>
              <span className="chat-file-sub">Sent in chat with {clientName}</span>
            </div>
          </div>

          <Select label="File it under" value={category} onChange={(e) => setCategory(e.target.value)}>
            {grouped.map((g) => (
              <optgroup key={g.id} label={g.name}>
                {g.items.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </optgroup>
            ))}
          </Select>

          <Select
            label="Attach it to"
            value={destType}
            onChange={(e) => { setDestType(e.target.value); setDestId(''); setTargetSearch(''); }}
          >
            {DESTINATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </Select>

          {destType && (
            <div className="ds-field">
              <label className="ds-field-label" htmlFor="chat-file-target-pick">
                Which one
              </label>
              {(data?.targets?.[destType] || []).length > 8 && (
                <input
                  className="ds-input chat-file-search"
                  placeholder="Filter…"
                  value={targetSearch}
                  onChange={(e) => setTargetSearch(e.target.value)}
                />
              )}
              <select
                id="chat-file-target-pick"
                className="ds-select chat-file-picklist"
                size={Math.min(8, Math.max(3, targets.length))}
                value={destId}
                onChange={(e) => setDestId(e.target.value)}
              >
                {targets.length === 0 && <option value="" disabled>Nothing to choose here yet</option>}
                {targets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}{t.hint ? ` — ${t.hint}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="chat-file-row">
            <Select label="Financial year (optional)" value={financialYear} onChange={(e) => setFinancialYear(e.target.value)}>
              <option value="">—</option>
              {fyOptions().map((fy) => <option key={fy} value={fy}>FY {fy}</option>)}
            </Select>
            <Input
              label="Period (optional)"
              placeholder="e.g. Jun-2026 or Q1"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            />
          </div>

          <Input
            label="Title in the document store"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <p className="chat-file-note">
            The file stays in this conversation — filing it only decides where else it appears in
            {' '}{clientName}'s records.
          </p>
        </div>
      )}
    </Modal>
  );
}

export default FileToClientModal;
